<script setup>
import { computed, ref, watch } from 'vue';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { scheduledSnoozeDateTime } from 'dashboard/helper/snoozeHelpers';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from 'dashboard/components-next/Conversation/ConversationCard/CardLabelsV5.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import UnreadBadge from 'dashboard/components-next/Conversation/ConversationCard/UnreadBadge.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  isPinned: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'click',
  'contextmenu',
  'selectConversation',
  'deSelectConversation',
]);

const hovered = ref(false);

const unreadCount = computed(() => props.chat.unread_count);
const hasUnread = computed(() => unreadCount.value > 0);
const lastMessageInChat = computed(() => getLastMessage(props.chat));
const contactAvatarIcon = computed(() => {
  const contactName = props.currentContact.name?.trim() || '';
  const isPhoneNumber = /^\+?[\d\s().-]{7,}$/.test(contactName);
  return !props.currentContact.thumbnail && isPhoneNumber
    ? 'i-lucide-user'
    : null;
});

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const showMetaSection = computed(() => {
  return (
    props.showInboxName ||
    (props.showAssignee && props.assignee.name) ||
    props.chat.priority
  );
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);
const hasActiveSla = computed(
  () =>
    Boolean(hasSlaPolicyId.value && props.chat?.applied_sla?.id) &&
    !props.currentContact?.blocked
);
const isAgentBotAssignee = computed(
  () =>
    props.chat?.meta?.assignee_type === 'AgentBot' ||
    Boolean(props.chat?.assignee_agent_bot_id)
);
const isAiActive = computed(
  () =>
    props.chat?.meta?.assignee_type === 'AgentBot' ||
    Boolean(props.chat?.assignee_agent_bot_id)
);
const aiActiveLabel = 'IA ativa';
const snoozedUntil = computed(
  () => props.chat?.snoozed_until || props.chat?.snoozedUntil
);
const scheduledAtLabel = computed(() =>
  scheduledSnoozeDateTime(snoozedUntil.value)
);

const showLabelsSection = computed(() => {
  return (
    props.chat.labels?.length > 0 ||
    hasActiveSla.value ||
    Boolean(scheduledAtLabel.value)
  );
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.inbox.id);
  } else {
    emit('deSelectConversation', props.chat.id, props.inbox.id);
  }
};

const selectedModel = computed({
  get: () => props.selected,
  set: value => onSelectConversation(value),
});

watch(
  () => props.chat.id,
  () => {
    hovered.value = false;
  }
);
</script>

<template>
  <div
    data-testid="conversation-card"
    class="conversation group relative mx-2 my-1 flex w-auto max-w-full flex-grow-0 flex-shrink-0 cursor-pointer items-center rounded-xl border border-transparent py-0 transition-colors hover:z-[1]"
    :class="{
      'active animate-card-select bg-n-blue-9/10': isActiveChat,
      'selected bg-n-slate-2 dark:bg-n-slate-3': selected,
      'hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3': !isActiveChat && !selected,
      'px-0': compact,
      'px-3': !compact,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        :icon-name="contactAvatarIcon"
        hide-offline-status
      >
        <template v-if="showInboxName && inbox.id" #badge>
          <span
            class="absolute z-20 flex items-center justify-center rounded-full bg-n-solid-1 border border-n-slate-3 size-4 -bottom-1 -right-1"
          >
            <ChannelIcon
              :inbox="inbox"
              colored
              use-brand-icon
              class="size-3.5"
            />
          </span>
        </template>
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <Checkbox v-model="selectedModel" />
          </label>
        </template>
      </Avatar>
    </div>
    <div class="px-0 py-3 flex-1 min-w-0 border-line">
      <div
        v-if="showMetaSection"
        class="flex items-center min-w-0 gap-1"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <InboxName
          v-if="showInboxName"
          :inbox="inbox"
          :show-icon="false"
          class="flex-1 min-w-0"
        />
        <div
          class="flex items-baseline gap-2 flex-shrink-0"
          :class="{
            'flex-1 justify-between': !showInboxName,
          }"
        >
          <span
            v-if="showAssignee && assignee.name"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center gap-px truncate"
          >
            <Icon
              :icon="
                isAgentBotAssignee ? 'i-lucide-bot' : 'i-lucide-user-round'
              "
              class="size-3 text-n-slate-11 flex-shrink-0"
            />
            <span class="truncate">{{ assignee.name }}</span>
          </span>
          <CardPriorityIcon
            :priority="chat.priority"
            class="flex-shrink-0 !size-3.5"
          />
        </div>
      </div>
      <h4
        class="conversation--user mx-2 mb-0.5 mt-0 h-5 min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap pt-0.5 text-[0.9375rem] leading-5 text-n-slate-12 ltr:pr-16 rtl:pl-16"
        :class="hasUnread ? 'font-semibold' : 'font-medium'"
      >
        {{ currentContact.name }}
      </h4>
      <div v-if="isAiActive" class="mx-2 mb-1 flex flex-wrap gap-1">
        <span
          v-if="isAiActive"
          class="inline-flex h-5 max-w-full items-center gap-1 rounded-md bg-n-teal-3 px-1.5 text-xs font-medium leading-none text-n-teal-11"
        >
          <span class="i-lucide-bot size-3 flex-shrink-0" />
          <span class="truncate">{{ aiActiveLabel }}</span>
        </span>
      </div>
      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        class="mx-2 my-0 h-5 min-w-0 flex-1 text-[0.8125rem] leading-5"
        :class="messagePreviewClass"
      />
      <p
        v-else
        key="no-messages"
        class="mx-2 my-0 h-5 min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[0.8125rem] leading-5 text-n-slate-11"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        class="absolute flex flex-col ltr:right-3 rtl:left-3"
        :class="showMetaSection ? 'top-8' : 'top-4'"
      >
        <span
          class="ml-auto inline-flex items-center gap-1 font-normal leading-4 text-xxs"
        >
          <span
            v-if="isPinned"
            v-tooltip.top="$t('CONVERSATION.CARD_CONTEXT_MENU.PINNED')"
            data-testid="pinned-conversation-badge"
            class="inline-flex h-5 flex-shrink-0 items-center gap-1 rounded-md border border-n-amber-5 bg-n-amber-3 px-1.5 text-xxs font-medium text-n-amber-11"
          >
            <Icon icon="i-ri-pushpin-fill" class="size-3" />
            {{ $t('CONVERSATION.CARD_CONTEXT_MENU.PINNED_BADGE') }}
          </span>
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :conversation-id="chat.id"
          />
        </span>
        <UnreadBadge
          v-if="hasUnread"
          :count="unreadCount"
          class="ltr:ml-auto rtl:mr-auto mt-1"
        />
      </div>
      <CardLabels
        v-if="showLabelsSection"
        :labels="chat.labels || []"
        class="mx-2 mb-0 mt-1"
      >
        <template v-if="scheduledAtLabel || hasActiveSla" #before>
          <span
            v-if="scheduledAtLabel"
            v-tooltip.top="{
              content: `Adiado até ${scheduledAtLabel}`,
              delay: { show: 500, hide: 0 },
            }"
            class="inline-flex items-center gap-1 ltr:mr-1 rtl:ml-1 px-1.5 h-5 max-w-full rounded-md border border-n-amber-5 bg-n-amber-2 text-n-amber-11 text-xs font-medium leading-none"
          >
            <span class="i-lucide-clock size-3 flex-shrink-0" />
            <span class="truncate">{{ scheduledAtLabel }}</span>
          </span>
          <SLACardLabel
            v-if="hasActiveSla"
            :chat="chat"
            class="ltr:mr-1 rtl:ml-1"
          />
        </template>
      </CardLabels>
    </div>
  </div>
</template>
